/**
 * MIDI Sight-Reading Trainer — Main Tool Interface
 * ================================================
 * Design: Studio Console (DAW-inspired dark interface)
 * - Deep charcoal base with warm amber accents
 * - WHITE sheet-music panel for notation (like real paper)
 * - Integrated metronome with timing evaluation (10% tolerance)
 * - JetBrains Mono for data, Space Grotesk for labels
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Music,
  ChevronRight,
  ChevronLeft,
  HelpCircle,
  Play,
  RotateCcw,
  SkipForward,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import StaffNotation from "@/components/StaffNotation";
import PianoKeyboard from "@/components/PianoKeyboard";
import MidiStatus from "@/components/MidiStatus";
import ControlPanel from "@/components/ControlPanel";
import StatsPanel from "@/components/StatsPanel";
import FeedbackOverlay from "@/components/FeedbackOverlay";
import ABRSMUploadDialog, { type ABRSMExerciseConfig } from "@/components/ABRSMUploadDialog";
import ErrorLogDialog, { type ErrorLogEntry } from "@/components/ErrorLogDialog";
import { useMidi, midiToNoteName } from "@/hooks/useMidi";
import { useGameState } from "@/hooks/useGameState";
import { useSound } from "@/hooks/useSound";
import { useMetronome, type TimingResult } from "@/hooks/useMetronome";
import {
  generateExercise,
  type Exercise,
  type Clef,
  type KeySignature,
  type TimeSignature,
  type Difficulty,
  type KeyboardRange,
} from "@/lib/exerciseGenerator";
import { importExerciseFromCxml } from "@/lib/cxmlImport";

type NoteState = "correct" | "wrong" | "pending";

interface PlayableNote {
  midi: number;
  clef: "treble" | "bass";
  globalBeatPosition: number;
  expectedDurationBeats: number;
  measureIndex: number;
  beatInMeasure: number;
}

export default function Home() {
  // MIDI
  const midi = useMidi();

  // Game state
  const game = useGameState();

  // Sound
  const sound = useSound();
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Metronome
  const metronome = useMetronome();
  const [metronomeEnabled, setMetronomeEnabled] = useState(true);

  // Exercise settings
  const [clef, setClef] = useState<Clef>("treble");
  const [keySignature, setKeySignature] = useState<KeySignature>("C");
  const [timeSignature, setTimeSignature] = useState<TimeSignature>("4/4");
  const [difficulty, setDifficulty] = useState<Difficulty>(1);
  const [tempoOverride, setTempoOverride] = useState<number | null>(null);

  // Exercise state
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(0);
  const [noteStates, setNoteStates] = useState<Map<number, NoteState>>(new Map());
  const [isPlaying, setIsPlaying] = useState(false);
  const [exerciseComplete, setExerciseComplete] = useState(false);
  const [playableNotes, setPlayableNotes] = useState<PlayableNote[]>([]);
  const [isCountingIn, setIsCountingIn] = useState(false);

  // Timing results
  const [timingResults, setTimingResults] = useState<TimingResult[]>([]);
  const [errorLog, setErrorLog] = useState<ErrorLogEntry[]>([]);
  const [lastTiming, setLastTiming] = useState<TimingResult | null>(null);

  // Per-exercise scoring (correct count tracked in a ref so completion never
  // reads a stale noteStates closure)
  const correctCountRef = useRef(0);
  const [exerciseAccuracy, setExerciseAccuracy] = useState(0);

  // Feedback
  const [feedback, setFeedback] = useState<"correct" | "wrong" | null>(null);
  const [feedbackExpected, setFeedbackExpected] = useState<string>("");
  const [feedbackPlayed, setFeedbackPlayed] = useState<string>("");
  const [feedbackErrorType, setFeedbackErrorType] = useState<"pitch" | "duration" | null>(null);
  const [feedbackExpectedDuration, setFeedbackExpectedDuration] = useState<number | null>(null);
  const [feedbackActualDuration, setFeedbackActualDuration] = useState<number | null>(null);
  const [feedbackExpectedBeats, setFeedbackExpectedBeats] = useState<number | null>(null);
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Active note tracking for duration-based evaluation (note on -> note off)
  const [awaitingRelease, setAwaitingRelease] = useState(false);
  const [noteStartTime, setNoteStartTime] = useState<number | null>(null);
  const currentNoteDataRef = useRef<{ midi: number; expectedDuration: number } | null>(null);
  const currentNoteTimingRef = useRef<TimingResult | null>(null);
  const queuedMidiNoteRef = useRef<number | null>(null);
  const syntheticReleaseRef = useRef<number | null>(null);
  const countInEndMsRef = useRef<number>(0);

  // UI state
  const [statsPanelOpen, setStatsPanelOpen] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [showErrorLog, setShowErrorLog] = useState(false);
  const [showABRSMDialog, setShowABRSMDialog] = useState(false);

  // Keyboard range (for smaller MIDI keyboards)
  const [keyboardRange, setKeyboardRange] = useState<KeyboardRange>("full");

  // Compute playable notes in the same order they are rendered onscreen.
  // For "both" clef mode, UI shows treble block first, then bass block.
  const computePlayableNotes = useCallback((ex: Exercise, targetClef: Clef): PlayableNote[] => {
    const notes: PlayableNote[] = [];

    const pushMeasureNotes = (measureClef: "treble" | "bass", beatOffset: number) => {
      ex.measures
        .filter((measure) => measure.clef === measureClef)
        .forEach((measure) => {
          measure.notes.forEach((note) => {
            if (note.rest) return;
            const prev = notes[notes.length - 1];
            if (note.tieStop && prev) {
              // Tied continuation: extend the previous note instead of adding a new one.
              prev.expectedDurationBeats += note.beats;
              return;
            }
            notes.push({
              midi: note.midi,
              clef: measure.clef,
              globalBeatPosition: beatOffset + note.globalBeatPosition,
              expectedDurationBeats: note.beats,
              measureIndex: measure.measureIndex,
              beatInMeasure: note.beatPosition,
            });
          });
        });
    };

    if (targetClef === "both") {
      // The two blocks are played sequentially against one continuously running
      // metronome, so the bass block's beat positions start where treble ends.
      pushMeasureNotes("treble", 0);
      pushMeasureNotes("bass", ex.totalBeats);
    } else {
      pushMeasureNotes(targetClef, 0);
    }

    return notes;
  }, []);

  const beginExerciseSession = useCallback((ex: Exercise) => {
    const beatsPerMeasure = parseInt(ex.timeSignature.split("/")[0]);
    const pNotes = computePlayableNotes(ex, ex.clef);
    setExercise(ex);
    setPlayableNotes(pNotes);
    setCurrentNoteIndex(0);
    setNoteStates(new Map());
    setIsPlaying(true);
    setExerciseComplete(false);
    setFeedback(null);
    setErrorLog([]);
    setTimingResults([]);
    setLastTiming(null);
    setAwaitingRelease(false);
    setNoteStartTime(null);
    currentNoteDataRef.current = null;
    currentNoteTimingRef.current = null;
    queuedMidiNoteRef.current = null;
    syntheticReleaseRef.current = null;
    correctCountRef.current = 0;
    setExerciseAccuracy(0);

    // Start metronome with 1-measure count-in
    if (metronomeEnabled) {
      setIsCountingIn(true);
      metronome.start(ex.tempo, beatsPerMeasure, 1);
      // Count-in lasts 1 measure
      const countInMs = (60000 / ex.tempo) * beatsPerMeasure;
      countInEndMsRef.current = performance.now() + countInMs;
      setTimeout(() => {
        setIsCountingIn(false);
      }, countInMs);
    } else {
      countInEndMsRef.current = 0;
    }
  }, [computePlayableNotes, metronome, metronomeEnabled]);

  // Start new exercise
  const startExercise = useCallback(() => {
    // Use current panel state as the single source of truth.
    const ex = generateExercise({
      clef,
      keySignature,
      timeSignature,
      difficulty,
      measureCount: difficulty <= 2 ? 4 : difficulty <= 4 ? 6 : 8,
      keyboardRange,
      tempo: tempoOverride ?? undefined,
    });
    beginExerciseSession(ex);
  }, [clef, keySignature, timeSignature, difficulty, keyboardRange, tempoOverride, beginExerciseSession]);

  const retryCurrentExercise = useCallback(() => {
    if (!exercise) return;
    beginExerciseSession(exercise);
  }, [exercise, beginExerciseSession]);

  // Shared per-note bookkeeping: note state, error log, scoring, advancing,
  // and exercise completion. Used by all three input paths.
  const finalizeNote = useCallback(
    (params: { correct: boolean; timing: TimingResult | null; playedMidi: number }) => {
      const { correct, timing, playedMidi } = params;
      const expectedNote = playableNotes[currentNoteIndex];
      if (!expectedNote) return;

      setNoteStates((prev) => {
        const next = new Map(prev);
        next.set(currentNoteIndex, correct ? "correct" : "wrong");
        return next;
      });

      if (timing) {
        setTimingResults((prev) => [...prev, timing]);
        setLastTiming(timing);
      }

      if (!correct) {
        setErrorLog((prev) => [
          ...prev,
          {
            noteIndex: currentNoteIndex,
            expectedNote: midiToNoteName(expectedNote.midi),
            playedNote: midiToNoteName(playedMidi),
            expectedMidi: expectedNote.midi,
            playedMidi,
            timing: timing || undefined,
            measure: expectedNote.measureIndex,
            beatInMeasure: expectedNote.beatInMeasure,
          },
        ]);
      }

      game.recordNote(correct);
      if (correct) correctCountRef.current += 1;

      const nextIndex = currentNoteIndex + 1;
      setCurrentNoteIndex(nextIndex);
      if (nextIndex >= playableNotes.length) {
        setExerciseComplete(true);
        metronome.stop();
        const accuracy =
          playableNotes.length > 0
            ? Math.round((correctCountRef.current / playableNotes.length) * 100)
            : 0;
        setExerciseAccuracy(accuracy);
        game.completeExercise(accuracy);
        if (soundEnabled) setTimeout(() => sound.playLevelUp(), 300);
      }
    },
    [currentNoteIndex, playableNotes, game, metronome, sound, soundEnabled]
  );

  // Handle note press (note on)
  const handleNoteInput = useCallback(
    (midiNote: number) => {
      if (!exercise || !isPlaying || exerciseComplete) return;

      // Accept exact-downbeat first-note presses during count-in transition.
      const inCountInGraceWindow = isCountingIn && performance.now() >= (countInEndMsRef.current - 120);
      if (isCountingIn && !inCountInGraceWindow) return;

      if (currentNoteIndex >= playableNotes.length) return;
      if (awaitingRelease) {
        // If two notes overlap slightly (legato crossover), queue the next note instead of dropping it.
        queuedMidiNoteRef.current = midiNote;
        return;
      }

      const expectedNote = playableNotes[currentNoteIndex];
      const isCorrectPitch = expectedNote.midi === midiNote;

      // If wrong pitch, mark wrong immediately
      if (!isCorrectPitch) {
        if (soundEnabled) sound.playWrong();

        setFeedback("wrong");
        setFeedbackExpected(midiToNoteName(expectedNote.midi));
        setFeedbackPlayed(midiToNoteName(midiNote));
        setFeedbackErrorType("pitch");
        setFeedbackExpectedDuration(null);
        setFeedbackActualDuration(null);
        setFeedbackExpectedBeats(null);
        currentNoteTimingRef.current = null;

        finalizeNote({ correct: false, timing: null, playedMidi: midiNote });

        if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
        feedbackTimeoutRef.current = setTimeout(() => {
          setFeedback(null);
        }, 600);

        return;
      }

      // Correct pitch - now wait for release to check duration
      setNoteStartTime(performance.now());
      setAwaitingRelease(true);
      currentNoteDataRef.current = {
        midi: midiNote,
        expectedDuration: exercise.beatDurationMs * expectedNote.expectedDurationBeats,
      };

      // Beat alignment is evaluated at note onset (not release).
      currentNoteTimingRef.current = metronome.state.isRunning
        ? metronome.evaluateTiming(expectedNote.globalBeatPosition, expectedNote.expectedDurationBeats)
        : null;

      if (soundEnabled) sound.playCorrect();
    },
    [exercise, isPlaying, exerciseComplete, isCountingIn, currentNoteIndex, playableNotes, awaitingRelease, sound, soundEnabled, metronome, finalizeNote]
  );

  // Handle note release (note off) - evaluate duration
  const handleNoteRelease = useCallback(
    (midiNote: number) => {
      if (!awaitingRelease || !noteStartTime || !currentNoteDataRef.current) return;
      if (midiNote !== currentNoteDataRef.current.midi) return;

      const noteDuration = performance.now() - noteStartTime;
      const expectedDuration = currentNoteDataRef.current.expectedDuration;
      const expectedNote = playableNotes[currentNoteIndex];

      // Calculate duration accuracy (tolerance: ±20% of expected duration)
      const durationDiff = Math.abs(noteDuration - expectedDuration);
      const tolerance = expectedDuration * 0.2; // 20% tolerance
      const isDurationCorrect = durationDiff <= tolerance;

      // Use timing captured at note-on for beat alignment display.
      let timing: TimingResult | null = currentNoteTimingRef.current;
      if (!timing && metronome.state.isRunning) {
        timing = metronome.evaluateTiming(expectedNote.globalBeatPosition, expectedNote.expectedDurationBeats);
      }

      setFeedback(isDurationCorrect ? "correct" : "wrong");
      setFeedbackExpected(midiToNoteName(expectedNote.midi));
      setFeedbackPlayed(midiToNoteName(expectedNote.midi));
      setFeedbackErrorType(isDurationCorrect ? null : "duration");
      setFeedbackExpectedDuration(expectedDuration);
      setFeedbackActualDuration(noteDuration);
      setFeedbackExpectedBeats(expectedNote.expectedDurationBeats);

      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => {
        setFeedback(null);
        setLastTiming(null);
      }, 1200);

      // Reset tracking state
      setNoteStartTime(null);
      setAwaitingRelease(false);
      currentNoteDataRef.current = null;
      currentNoteTimingRef.current = null;

      finalizeNote({ correct: isDurationCorrect, timing, playedMidi: midiNote });
    },
    [awaitingRelease, noteStartTime, currentNoteIndex, playableNotes, metronome, finalizeNote]
  );

  // Process queued MIDI note-on after a held note finalizes.
  useEffect(() => {
    if (awaitingRelease) return;
    if (!exercise || !isPlaying || exerciseComplete || isCountingIn) return;

    const queued = queuedMidiNoteRef.current;
    if (queued === null) return;

    queuedMidiNoteRef.current = null;

    // If the key was already released, flag a synthetic release. It is handled
    // by the effect below once React re-renders with the new hold state, so the
    // release handler sees fresh state instead of a stale closure.
    const expectedMidi = currentNoteIndex < playableNotes.length ? playableNotes[currentNoteIndex].midi : null;
    if (queued === expectedMidi && !midi.activeNotes.has(queued)) {
      syntheticReleaseRef.current = queued;
    }

    handleNoteInput(queued);
  }, [awaitingRelease, exercise, isPlaying, exerciseComplete, isCountingIn, currentNoteIndex, playableNotes, midi.activeNotes, handleNoteInput]);

  // Deliver a pending synthetic release once the new note's hold state is live.
  useEffect(() => {
    if (!awaitingRelease || syntheticReleaseRef.current === null) return;
    const note = syntheticReleaseRef.current;
    syntheticReleaseRef.current = null;
    handleNoteRelease(note);
  }, [awaitingRelease, handleNoteRelease]);

  // Virtual keyboard uses note-down/note-up to preserve duration-based timing logic.
  const handleVirtualKeyboardNoteDown = useCallback(
    (midiNote: number) => {
      if (!exercise || !isPlaying || exerciseComplete || isCountingIn) return;
      if (currentNoteIndex >= playableNotes.length) return;

      const expectedNote = playableNotes[currentNoteIndex];

      // Wrong pitch: reuse existing immediate wrong-note pipeline.
      if (expectedNote.midi !== midiNote) {
        handleNoteInput(midiNote);
        return;
      }

      // Mouse-only behavior: evaluate timing on click (no hold required).
      const timing = metronome.state.isRunning
        ? metronome.evaluateTiming(expectedNote.globalBeatPosition, expectedNote.expectedDurationBeats)
        : null;

      const isTimingCorrect = timing ? timing.onTime : true;

      setFeedback(isTimingCorrect ? "correct" : "wrong");
      setFeedbackExpected(midiToNoteName(expectedNote.midi));
      setFeedbackPlayed(midiToNoteName(midiNote));
      setFeedbackErrorType(isTimingCorrect ? null : "duration");
      setFeedbackExpectedDuration(exercise.beatDurationMs * expectedNote.expectedDurationBeats);
      setFeedbackActualDuration(exercise.beatDurationMs * expectedNote.expectedDurationBeats);
      setFeedbackExpectedBeats(expectedNote.expectedDurationBeats);

      // Ensure mouse interactions never get blocked by hold-state tracking.
      setAwaitingRelease(false);
      setNoteStartTime(null);
      currentNoteDataRef.current = null;
      currentNoteTimingRef.current = null;

      finalizeNote({ correct: isTimingCorrect, timing, playedMidi: midiNote });

      if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
      feedbackTimeoutRef.current = setTimeout(() => {
        setFeedback(null);
        setLastTiming(null);
      }, 1200);

      if (soundEnabled) {
        if (isTimingCorrect) sound.playCorrect();
        else sound.playWrong();
      }
    },
    [exercise, isPlaying, exerciseComplete, isCountingIn, currentNoteIndex, playableNotes, handleNoteInput, metronome, soundEnabled, sound, finalizeNote]
  );

  const handleVirtualKeyboardNoteUp = useCallback(
    (midiNote: number) => {
      if (!exercise || !isPlaying || exerciseComplete || isCountingIn) return;
      handleNoteRelease(midiNote);
    },
    [exercise, isPlaying, exerciseComplete, isCountingIn, handleNoteRelease]
  );

  // MIDI input handlers
  useEffect(() => {
    midi.onNoteOn((note) => {
      handleNoteInput(note.note);
    });
    midi.onNoteOff((note) => {
      handleNoteRelease(note);
    });
  }, [midi, handleNoteInput, handleNoteRelease]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore when an interactive control has focus (Space would also activate it)
      if (e.target instanceof HTMLElement && e.target.closest("button, input, select, textarea, [contenteditable], [role='combobox']")) return;
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        if (!isPlaying || exerciseComplete) {
          startExercise();
        }
      }
      if (e.key === "Escape") {
        setShowHelp(false);
        setShowABRSMDialog(false);
        setShowErrorLog(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPlaying, exerciseComplete, startExercise]);

  // Current expected note for highlighting
  const currentExpectedMidi =
    exercise && isPlaying && !exerciseComplete && !isCountingIn && currentNoteIndex < playableNotes.length
      ? playableNotes[currentNoteIndex].midi
      : null;

  // Handle ABRSM upload
  const handleABRSMUpload = useCallback(async (config: ABRSMExerciseConfig) => {
    const importedExercise = await importExerciseFromCxml({
      file: config.cxmlFile,
      difficulty: config.difficulty,
      tempoHint: config.tempoHint,
      startBar: config.startBar,
      endBar: config.endBar,
    });

    setClef(importedExercise.clef);
    setKeySignature(importedExercise.keySignature);
    setTimeSignature(importedExercise.timeSignature);
    setDifficulty(config.difficulty);
    setTempoOverride(importedExercise.tempo);

    beginExerciseSession(importedExercise);
  }, [beginExerciseSession]);

  // Timing stats summary
  const timingAvg = timingResults.length > 0
    ? Math.round(timingResults.reduce((a, t) => a + Math.abs(t.offsetMs), 0) / timingResults.length)
    : 0;
  const timingOnTimeCount = timingResults.filter(t => t.onTime).length;

  // Keep virtual keyboard range aligned with current exercise so all target notes are reachable onscreen.
  const [displayStartNote, displayEndNote] = useMemo(() => {
    if (playableNotes.length === 0) {
      return [
        clef === "bass" ? 36 : clef === "both" ? 36 : 48,
        clef === "bass" ? 64 : 84,
      ] as const;
    }

    const midiNotes = playableNotes.map((note) => note.midi);
    let start = Math.max(21, Math.min(...midiNotes) - 3);
    let end = Math.min(108, Math.max(...midiNotes) + 3);

    // Ensure a practical visible width (at least ~2 octaves).
    if (end - start < 24) {
      const center = Math.round((start + end) / 2);
      start = Math.max(21, center - 12);
      end = Math.min(108, start + 24);
      if (end - start < 24) {
        start = Math.max(21, end - 24);
      }
    }

    return [start, end] as const;
  }, [playableNotes, clef]);

  return (
    <div
      className="h-screen w-screen flex flex-col overflow-hidden select-none"
      style={{ background: "oklch(0.14 0.015 270)" }}
    >
      {/* ===== TOP BAR ===== */}
      <header
        className="flex items-center justify-between px-4 py-2 border-b shrink-0"
        style={{
          background: "oklch(0.16 0.015 270)",
          borderColor: "oklch(0.24 0.010 270)",
        }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="w-7 h-7 rounded flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, oklch(0.82 0.17 75), oklch(0.70 0.15 60))",
            }}
          >
            <Music size={15} style={{ color: "oklch(0.16 0.015 270)" }} />
          </div>
          <div>
            <h1
              className="text-sm font-bold leading-none"
              style={{ color: "oklch(0.90 0.01 270)", fontFamily: "'Space Grotesk', sans-serif" }}
            >
              MIDI Sight-Reader
            </h1>
            <span className="text-[10px] font-mono" style={{ color: "oklch(0.45 0.01 270)" }}>
              v2.1 — Sight-Reading Trainer
            </span>
          </div>
        </div>

        <MidiStatus
          isSupported={midi.isSupported}
          isConnected={midi.isConnected}
          activeDevice={midi.activeDevice}
          devices={midi.devices}
          activeNotes={midi.activeNotes}
          onDeviceSelect={midi.connectDevice}
          error={midi.error}
        />

        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={() => setShowHelp(!showHelp)}
            title="Help"
          >
            <HelpCircle size={14} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={() => setStatsPanelOpen(!statsPanelOpen)}
            title={statsPanelOpen ? "Hide stats" : "Show stats"}
          >
            {statsPanelOpen ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </Button>
        </div>
      </header>

      {/* ===== CONTROLS BAR ===== */}
      <div
        className="px-4 py-2 border-b shrink-0"
        style={{
          background: "oklch(0.17 0.015 270)",
          borderColor: "oklch(0.24 0.010 270)",
        }}
      >
        <ControlPanel
          clef={clef}
          keySignature={keySignature}
          timeSignature={timeSignature}
          difficulty={difficulty}
          keyboardRange={keyboardRange}
          tempoValue={tempoOverride === null ? "auto" : String(tempoOverride)}
          isPlaying={isPlaying}
          soundEnabled={soundEnabled}
          onClefChange={setClef}
          onKeyChange={setKeySignature}
          onTimeChange={setTimeSignature}
          onDifficultyChange={setDifficulty}
          onKeyboardRangeChange={setKeyboardRange}
          onTempoChange={(value) => setTempoOverride(value === "auto" ? null : Number(value))}
          onStart={startExercise}
          onNext={startExercise}
          onReset={() => {
            game.resetSession();
            metronome.stop();
            setExercise(null);
            setPlayableNotes([]);
            setIsPlaying(false);
            setExerciseComplete(false);
            setCurrentNoteIndex(0);
            setNoteStates(new Map());
            setTimingResults([]);
            setLastTiming(null);
            setErrorLog([]);
            setExerciseAccuracy(0);
            correctCountRef.current = 0;
            queuedMidiNoteRef.current = null;
            syntheticReleaseRef.current = null;
          }}
          onSoundToggle={() => setSoundEnabled(!soundEnabled)}
          metronomeEnabled={metronomeEnabled}
          tolerance={metronome.tolerance}
          onToleranceChange={metronome.setTolerance}
          onMetronomeToggle={() => {
            setMetronomeEnabled(!metronomeEnabled);
            if (metronomeEnabled && metronome.state.isRunning) {
              metronome.setMuted(true);
            } else {
              metronome.setMuted(false);
            }
          }}
          onABRSMUpload={() => setShowABRSMDialog(true)}
        />
      </div>

      {/* ===== MAIN CONTENT ===== */}
      <div className="flex flex-1 overflow-hidden">
        {/* Center: Notation Area */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          <div className="flex-1 overflow-y-auto p-4 relative">
            {!exercise ? (
              <EmptyState midiConnected={midi.isConnected} />
            ) : (
              <div className="relative">
                {/* Feedback overlay */}
                <FeedbackOverlay
                  errorType={feedbackErrorType}
                  expectedDuration={feedbackExpectedDuration || undefined}
                  actualDuration={feedbackActualDuration || undefined}
                  expectedBeats={feedbackExpectedBeats || undefined}
                  feedback={feedback}
                  expectedNote={feedbackExpected}
                  playedNote={feedbackPlayed}
                  timingResult={lastTiming}
                />

                {/* Count-in overlay */}
                <AnimatePresence>
                  {isCountingIn && metronome.state.isRunning && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 z-30 flex items-center justify-center pointer-events-none"
                    >
                      <motion.div
                        key={metronome.state.currentBeat}
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 1.5, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="text-6xl font-bold font-mono"
                        style={{ color: "oklch(0.82 0.17 75)" }}
                      >
                        {metronome.state.currentBeat > 0 ? metronome.state.currentBeat : ""}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Exercise complete banner */}
                <AnimatePresence>
                  {exerciseComplete && (
                    <motion.div
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 p-5 rounded-lg text-center"
                      style={{
                        background: "oklch(0.19 0.015 270)",
                        border: "1px solid oklch(0.82 0.17 75 / 30%)",
                        boxShadow: "0 0 30px oklch(0.82 0.17 75 / 10%)",
                      }}
                    >
                      <div className="text-lg font-bold mb-1" style={{ color: "oklch(0.82 0.17 75)" }}>
                        Exercise Complete!
                      </div>
                      <div className="text-sm font-mono mb-1" style={{ color: "oklch(0.70 0.01 270)" }}>
                        Accuracy: {exerciseAccuracy}% — Best Streak: {game.session.bestStreak}
                      </div>
                      {timingResults.length > 0 && (
                        <div className="text-xs font-mono mb-1" style={{ color: "oklch(0.82 0.14 200)" }}>
                          Timing: {timingOnTimeCount}/{timingResults.length} on-time — Avg deviation: {timingAvg}ms
                        </div>
                      )}
                      <div className="text-xs font-mono" style={{ color: "oklch(0.50 0.01 270)" }}>
                        +{game.session.xpEarned} XP earned this session
                      </div>
                      <div className="flex items-center justify-center gap-2 mt-3">
                        <Button size="sm" className="gap-1.5" onClick={startExercise}>
                          <Play size={13} />
                          Next Exercise
                        </Button>
                        <Button size="sm" variant="secondary" className="gap-1.5" onClick={retryCurrentExercise}>
                          <RotateCcw size={13} />
                          Reattempt
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="gap-1.5"
                          onClick={() => setShowErrorLog(true)}
                        >
                          <XCircle size={13} />
                          View Errors ({errorLog.length})
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Progress bar + beat indicator */}
                {isPlaying && !exerciseComplete && (
                  <div className="mb-3 flex items-center gap-3">
                    <span className="text-[10px] font-mono shrink-0" style={{ color: "oklch(0.50 0.01 270)" }}>
                      {currentNoteIndex}/{playableNotes.length}
                    </span>
                    <div
                      className="flex-1 h-1.5 rounded-full overflow-hidden"
                      style={{ background: "oklch(0.22 0.012 270)" }}
                    >
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: "linear-gradient(90deg, oklch(0.82 0.17 75), oklch(0.82 0.14 200))" }}
                        animate={{ width: `${(currentNoteIndex / playableNotes.length) * 100}%` }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      />
                    </div>

                    {/* Beat indicator */}
                    {metronome.state.isRunning && (
                      <div className="flex items-center gap-1 shrink-0">
                        {Array.from({ length: metronome.state.beatsPerMeasure }, (_, i) => (
                          <motion.div
                            key={i}
                            className="w-2.5 h-2.5 rounded-full"
                            animate={{
                              backgroundColor: i + 1 === metronome.state.currentBeat
                                ? "#ffd54f"
                                : "#4b5563",
                              scale: i + 1 === metronome.state.currentBeat ? 1.3 : 1,
                            }}
                            transition={{ duration: 0.08 }}
                          />
                        ))}
                      </div>
                    )}

                    {currentExpectedMidi !== null && (
                      <motion.span
                        key={currentExpectedMidi}
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-xs font-mono px-2 py-0.5 rounded shrink-0"
                        style={{
                          background: "oklch(0.82 0.14 200 / 15%)",
                          color: "oklch(0.82 0.14 200)",
                          border: "1px solid oklch(0.82 0.14 200 / 20%)",
                        }}
                      >
                        Play: {midiToNoteName(currentExpectedMidi)}
                      </motion.span>
                    )}
                  </div>
                )}

                {/* Notation — white background like real sheet music */}
                <div className="rounded-lg overflow-hidden" style={{ boxShadow: "0 2px 12px oklch(0 0 0 / 30%)" }}>
                  <StaffNotation
                    exercise={exercise}
                    currentNoteIndex={currentNoteIndex}
                    noteStates={noteStates}
                    clef={exercise.clef}
                  />
                </div>

                {/* Exercise metadata */}
                <div className="mt-3 flex items-center gap-4 flex-wrap">
                  <MetaTag label="Key" value={`${exercise.keySignature} Major`} />
                  <MetaTag label="Time" value={exercise.timeSignature} />
                  <MetaTag label="Tempo" value={`${exercise.tempo} BPM`} />
                  <MetaTag label="Notes" value={String(playableNotes.length)} />
                  <MetaTag label="Measures" value={String(exercise.measures.filter(m => m.clef === (exercise.clef === "both" ? "treble" : exercise.clef)).length)} />
                  {metronomeEnabled && (
                    <MetaTag label="Metronome" value="ON" accent />
                  )}
                </div>

                {/* Timing feedback strip */}
                {lastTiming && !exerciseComplete && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-2 flex items-center gap-2"
                  >
                    <span
                      className="text-[10px] font-mono px-2 py-0.5 rounded"
                      style={{
                        background: lastTiming.onTime ? "oklch(0.70 0.18 150 / 15%)" : "oklch(0.65 0.24 25 / 15%)",
                        color: lastTiming.onTime ? "oklch(0.70 0.18 150)" : "oklch(0.65 0.24 25)",
                        border: `1px solid ${lastTiming.onTime ? "oklch(0.70 0.18 150 / 25%)" : "oklch(0.65 0.24 25 / 25%)"}`,
                      }}
                    >
                      {lastTiming.rating.toUpperCase()} — {lastTiming.offsetMs > 0 ? "+" : ""}{Math.round(lastTiming.offsetMs)}ms
                    </span>
                  </motion.div>
                )}
              </div>
            )}
          </div>

          {/* Piano Keyboard */}
          <div
            className="px-4 py-2.5 border-t shrink-0"
            style={{ borderColor: "oklch(0.24 0.010 270)", background: "oklch(0.15 0.015 270)" }}
          >
            <PianoKeyboard
              activeNotes={midi.activeNotes}
              highlightNote={currentExpectedMidi}
              onNoteDown={handleVirtualKeyboardNoteDown}
              onNoteUp={handleVirtualKeyboardNoteUp}
              startNote={displayStartNote}
              endNote={displayEndNote}
            />
          </div>
        </div>

        {/* Right: Stats Panel */}
        <AnimatePresence>
          {statsPanelOpen && (
            <motion.aside
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 260, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-l overflow-hidden shrink-0"
              style={{
                background: "oklch(0.16 0.015 270)",
                borderColor: "oklch(0.24 0.010 270)",
              }}
            >
              <div className="w-[260px] h-full p-3">
                <StatsPanel
                  gameState={game.gameState}
                  session={game.session}
                  newAchievements={game.newAchievements}
                  showLevelUp={game.showLevelUp}
                  allAchievements={game.allAchievements}
                  timingResults={timingResults}
                />
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>

      {/* ===== HELP MODAL ===== */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: "oklch(0 0 0 / 60%)" }}
            onClick={() => setShowHelp(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="panel p-6 max-w-lg mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-base font-bold mb-4" style={{ color: "oklch(0.90 0.01 270)" }}>
                How to Use MIDI Sight-Reader
              </h3>
              <div className="space-y-3 text-sm" style={{ color: "oklch(0.65 0.01 270)" }}>
                <HelpItem
                  num="1"
                  title="Connect MIDI"
                  desc="Plug in your MIDI keyboard via USB. The app will detect it automatically and show status in the toolbar."
                />
                <HelpItem
                  num="2"
                  title="Choose Settings"
                  desc="Pick Clef (treble/bass), Key, Time Signature, and Difficulty (1-5). Higher difficulty = wider note range, more complex rhythms."
                />
                <HelpItem
                  num="3"
                  title="Play Notes"
                  desc="Press START or Space. After a count-in, play each note for its full duration. Notes are marked correct when held for the correct number of beats."
                />
                <HelpItem
                  num="4"
                  title="Timing Feedback"
                  desc="After each note you'll see detailed feedback showing if the pitch was wrong or if the duration was too short or too long."
                />
                <HelpItem
                  num="5"
                  title="Earn XP & Level Up"
                  desc="Correct notes (right pitch + right timing) earn 10 XP + streak bonuses. Build streaks for multiplied XP."
                />
                <HelpItem
                  num="6"
                  title="ABRSM Exercises"
                  desc="Click the ABRSM button to import a cXML exercise, choose grade-based difficulty, and optionally practice only a selected bar range."
                />
              </div>
              <div className="mt-4 pt-3 border-t flex flex-wrap gap-3" style={{ borderColor: "oklch(0.28 0.010 270)" }}>
                <KbdHint keys="Space" desc="Start / New exercise" />
                <KbdHint keys="Esc" desc="Close dialogs" />
              </div>
              <Button size="sm" className="mt-4 w-full" onClick={() => setShowHelp(false)}>
                Got it
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== ABRSM UPLOAD DIALOG ===== */}
      <ABRSMUploadDialog
        isOpen={showABRSMDialog}
        onClose={() => setShowABRSMDialog(false)}
        onUpload={handleABRSMUpload}
      />

      {/* ===== ERROR LOG DIALOG ===== */}
      <ErrorLogDialog
        isOpen={showErrorLog}
        onClose={() => setShowErrorLog(false)}
        errors={errorLog}
        totalNotes={playableNotes.length}
        accuracy={exerciseAccuracy}
      />
    </div>
  );
}

/* ===== Sub-components ===== */

function EmptyState({ midiConnected }: { midiConnected: boolean }) {
  return (
    <div className="h-full flex flex-col items-center justify-center gap-4">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <div
          className="w-16 h-16 rounded-xl mx-auto mb-4 flex items-center justify-center"
          style={{
            background: "oklch(0.19 0.015 270)",
            border: "1px solid oklch(0.28 0.010 270)",
            boxShadow: "inset 0 2px 4px oklch(0 0 0 / 20%)",
          }}
        >
          <Music size={28} style={{ color: "oklch(0.45 0.01 270)" }} />
        </div>
        <h2 className="text-lg font-semibold mb-1.5" style={{ color: "oklch(0.70 0.01 270)" }}>
          Ready to Practice
        </h2>
        <p className="text-sm max-w-sm mx-auto" style={{ color: "oklch(0.45 0.01 270)" }}>
          Configure your settings above and press{" "}
          <Kbd>Start</Kbd> or hit <Kbd>Space</Kbd>
        </p>
        <p className="text-xs mt-2 max-w-xs mx-auto" style={{ color: "oklch(0.38 0.01 270)" }}>
          The metronome will count in 1 measure, then evaluate your timing. Adjust tolerance in the toolbar.
        </p>
        {!midiConnected && (
          <p className="text-xs mt-2 max-w-xs mx-auto" style={{ color: "oklch(0.38 0.01 270)" }}>
            Connect a MIDI keyboard for the best experience, or use the virtual keyboard below.
          </p>
        )}
      </motion.div>
    </div>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      className="px-1.5 py-0.5 rounded text-xs font-mono inline-block"
      style={{
        background: "oklch(0.22 0.012 270)",
        border: "1px solid oklch(0.30 0.010 270)",
        color: "oklch(0.82 0.17 75)",
      }}
    >
      {children}
    </kbd>
  );
}

function MetaTag({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <span className="text-[10px] font-mono" style={{ color: accent ? "oklch(0.70 0.18 150)" : "oklch(0.40 0.01 270)" }}>
      <span style={{ color: accent ? "oklch(0.55 0.14 150)" : "oklch(0.50 0.01 270)" }}>{label}:</span> {value}
    </span>
  );
}

function HelpItem({ num, title, desc }: { num: string; title: string; desc: string }) {
  return (
    <div className="flex gap-3">
      <span
        className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono font-bold shrink-0 mt-0.5"
        style={{ background: "oklch(0.82 0.17 75 / 15%)", color: "oklch(0.82 0.17 75)" }}
      >
        {num}
      </span>
      <div>
        <strong style={{ color: "oklch(0.82 0.17 75)" }}>{title}</strong>
        <p className="mt-0.5 text-xs leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function KbdHint({ keys, desc }: { keys: string; desc: string }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px]" style={{ color: "oklch(0.50 0.01 270)" }}>
      <kbd
        className="px-1.5 py-0.5 rounded font-mono"
        style={{ background: "oklch(0.22 0.012 270)", border: "1px solid oklch(0.30 0.010 270)", color: "oklch(0.65 0.01 270)" }}
      >
        {keys}
      </kbd>
      {desc}
    </div>
  );
}
